/**
 * The transition and texture half of the library — the port of `FXLibrary.swift`: twenty-one
 * effects covering the things a beat actually needs between sections — builds, drops,
 * impacts, sweeps, a looping vinyl bed, a tape stop, a glitch burst — plus three synthetic
 * vocal-style chops.
 *
 * Several of these cannot be expressed as a single patch, and that is deliberate rather than
 * a shortcoming: a reverse swell is a decay played backwards, a tape stop is a resampler
 * whose rate falls to zero, a vinyl bed is a Poisson process. Each of those is built here
 * from the same primitives the app already ships, on top of a rendered patch.
 */

import { makeEffectChain, makeEffectNode, type PadColor } from '../../model/types';
import {
  Biquad,
  applyEffectChain,
  crossfadeLoop,
  frameCount,
  interpolateCatmullRom,
  panGains,
  reverseChannels,
  type RenderedAudio,
} from '../offline-render';
import {
  makeSynthEnvelope,
  makeSynthFilter,
  makeSynthLFO,
  makeSynthLayer,
  makeSynthPatch,
  makeSynthPitchEnvelope,
  percussionPatch,
  renderPatch,
  type SynthPatch,
} from '../synth';
import {
  applyEdgeFades,
  applyFormantBank,
  finishToRMS,
  libraryRandom,
  librarySeedNumber,
  makeLibraryEntry,
  type LibraryEntry,
  type LibraryRenderContext,
} from './entry';
import { librarySeed } from './identity';
import { renderInstrumentNote } from './melodic';
import { mixInto } from './renderer';

export type LibraryEffect =
  | 'riserShort'
  | 'riserLong'
  | 'downlifter'
  | 'impact'
  | 'subDrop'
  | 'reverseSwell'
  | 'noiseSweepUp'
  | 'noiseSweepDown'
  | 'vinylCrackle'
  | 'tapeStop'
  | 'glitchStutter'
  | 'telephoneBlip'
  | 'laser'
  | 'zap'
  | 'siren'
  | 'staticBurst'
  | 'metalClank'
  | 'powerDown'
  | 'voxChopAh'
  | 'voxChopRise'
  | 'voxAir';

export const LIBRARY_EFFECTS: readonly LibraryEffect[] = [
  'riserShort',
  'riserLong',
  'downlifter',
  'impact',
  'subDrop',
  'reverseSwell',
  'noiseSweepUp',
  'noiseSweepDown',
  'vinylCrackle',
  'tapeStop',
  'glitchStutter',
  'telephoneBlip',
  'laser',
  'zap',
  'siren',
  'staticBurst',
  'metalClank',
  'powerDown',
  'voxChopAh',
  'voxChopRise',
  'voxAir',
];

function effectDisplayName(effect: LibraryEffect): string {
  switch (effect) {
    case 'riserShort':
      return 'Riser Short';
    case 'riserLong':
      return 'Riser Long';
    case 'downlifter':
      return 'Downlifter';
    case 'impact':
      return 'Impact Deep';
    case 'subDrop':
      return 'Sub Drop';
    case 'reverseSwell':
      return 'Reverse Swell';
    case 'noiseSweepUp':
      return 'Sweep Up';
    case 'noiseSweepDown':
      return 'Sweep Down';
    case 'vinylCrackle':
      return 'Vinyl Crackle';
    case 'tapeStop':
      return 'Tape Stop';
    case 'glitchStutter':
      return 'Glitch Stutter';
    case 'telephoneBlip':
      return 'Telephone Blip';
    case 'laser':
      return 'Laser Pew';
    case 'zap':
      return 'Zap Crackle';
    case 'siren':
      return 'Siren Sweep';
    case 'staticBurst':
      return 'Static Burst';
    case 'metalClank':
      return 'Metal Clank';
    case 'powerDown':
      return 'Power Down';
    case 'voxChopAh':
      return 'Synth Vox Chop';
    case 'voxChopRise':
      return 'Synth Vox Rise';
    case 'voxAir':
      return 'Synth Vox Air';
  }
}

function effectToken(effect: LibraryEffect): string {
  switch (effect) {
    case 'riserShort':
      return 'riser-short';
    case 'riserLong':
      return 'riser-long';
    case 'downlifter':
      return 'downlifter';
    case 'impact':
      return 'impact-deep';
    case 'subDrop':
      return 'sub-drop';
    case 'reverseSwell':
      return 'reverse-swell';
    case 'noiseSweepUp':
      return 'sweep-up';
    case 'noiseSweepDown':
      return 'sweep-down';
    case 'vinylCrackle':
      return 'vinyl-crackle';
    case 'tapeStop':
      return 'tape-stop';
    case 'glitchStutter':
      return 'glitch-stutter';
    case 'telephoneBlip':
      return 'telephone-blip';
    case 'laser':
      return 'laser-pew';
    case 'zap':
      return 'zap-crackle';
    case 'siren':
      return 'siren-sweep';
    case 'staticBurst':
      return 'static-burst';
    case 'metalClank':
      return 'metal-clank';
    case 'powerDown':
      return 'power-down';
    case 'voxChopAh':
      return 'vox-chop-ah';
    case 'voxChopRise':
      return 'vox-chop-rise';
    case 'voxAir':
      return 'vox-air';
  }
}

function effectCaption(effect: LibraryEffect): string {
  switch (effect) {
    case 'riserShort':
      return 'Two-second build. Noise and a detuned saw climbing together through an opening band-pass.';
    case 'riserLong':
      return 'Four-second build with an echo behind it, for the last bar before a drop.';
    case 'downlifter':
      return 'Falling transition. Three octaves of pitch drop while the filter closes down behind it.';
    case 'impact':
      return 'Deep impact. A sub hit with a bright transient and a long, dark room around it.';
    case 'subDrop':
      return 'Sub drop. A sine sliding from bass down to almost nothing. Felt more than heard.';
    case 'reverseSwell':
      return 'Reverse cymbal-style swell. A metallic decay played backwards, so it lands on the downbeat.';
    case 'noiseSweepUp':
      return 'Noise sweep up. Filtered air rising across the stereo image.';
    case 'noiseSweepDown':
      return 'Noise sweep down. The same air falling away out of a section.';
    case 'vinylCrackle':
      return 'Vinyl crackle. Surface noise, rumble and random pops, built to loop seamlessly under a beat.';
    case 'tapeStop':
      return 'Tape stop. A held chord dragged to a standstill, losing top end as it slows.';
    case 'glitchStutter':
      return 'Glitch stutter burst. Sixteenth-note beat repeat, then slice reversal and pitch jumps over the top.';
    case 'telephoneBlip':
      return 'Telephone-style blip. Two short tones through a 300–3200 Hz band and a bit crusher.';
    case 'laser':
      return 'Laser. A detuned saw falling four octaves with a short slap echo.';
    case 'zap':
      return 'Zap. An electric snap with a fast square modulation buzzing through it.';
    case 'siren':
      return 'Siren sweep. A saw wailing up and down under a slow pitch LFO.';
    case 'staticBurst':
      return 'Static burst. Crushed, downsampled noise, like a dropout or a radio tuning between stations.';
    case 'metalClank':
      return 'Metal clank. Three inharmonic squares through a ringing band-pass, in a small metal room.';
    case 'powerDown':
      return 'Power down. Pitch and filter collapsing together, crushed as they go.';
    case 'voxChopAh':
      return 'Synthetic “ah” chop. Not a recording: a formant-filtered saw, cut short and slapped with a delay.';
    case 'voxChopRise':
      return 'Synthetic vocal rise. The formant shifter pushed five semitones up, with the pitch scooping in.';
    case 'voxAir':
      return 'Synthetic vocal air. Breath noise through an “ee” vocal-tract filter, swelling and gone.';
  }
}

function effectTags(effect: LibraryEffect): string[] {
  switch (effect) {
    case 'riserShort':
    case 'riserLong':
      return ['fx', 'transition', 'riser', 'build'];
    case 'downlifter':
      return ['fx', 'transition', 'downlifter'];
    case 'impact':
      return ['fx', 'impact', 'cinematic'];
    case 'subDrop':
      return ['fx', 'impact', 'sub', '808'];
    case 'reverseSwell':
      return ['fx', 'transition', 'reverse'];
    case 'noiseSweepUp':
    case 'noiseSweepDown':
      return ['fx', 'transition', 'sweep', 'noise'];
    case 'vinylCrackle':
      return ['fx', 'texture', 'vinyl', 'lo-fi', 'loop'];
    case 'tapeStop':
      return ['fx', 'transition', 'tape'];
    case 'glitchStutter':
      return ['fx', 'glitch', 'stutter'];
    case 'telephoneBlip':
      return ['fx', 'ui', 'telephone'];
    case 'laser':
      return ['fx', 'sci-fi', 'laser'];
    case 'zap':
      return ['fx', 'sci-fi', 'zap'];
    case 'siren':
      return ['fx', 'siren', 'transition'];
    case 'staticBurst':
      return ['fx', 'noise', 'lo-fi'];
    case 'metalClank':
      return ['fx', 'impact', 'metal', 'percussion'];
    case 'powerDown':
      return ['fx', 'sci-fi', 'downlifter'];
    case 'voxChopAh':
    case 'voxChopRise':
    case 'voxAir':
      return ['fx', 'synthetic vocal', 'chop'];
  }
}

/**
 * One colour for the whole FX family and one for the synthetic vocals. Colour is a signal in
 * PadLab, so it groups; it never labels an individual sound.
 */
function effectColor(effect: LibraryEffect): PadColor {
  return effect === 'voxChopAh' || effect === 'voxChopRise' || effect === 'voxAir' ? 'magenta' : 'acid';
}

function effectSymbol(effect: LibraryEffect): string {
  switch (effect) {
    case 'riserShort':
    case 'riserLong':
      return 'chart.line.uptrend.xyaxis';
    case 'downlifter':
    case 'powerDown':
      return 'chart.line.downtrend.xyaxis';
    case 'impact':
    case 'metalClank':
      return 'burst.fill';
    case 'subDrop':
      return 'arrow.down.circle.fill';
    case 'reverseSwell':
      return 'arrow.uturn.backward';
    case 'noiseSweepUp':
    case 'noiseSweepDown':
    case 'siren':
      return 'wind';
    case 'vinylCrackle':
      return 'circle.dotted';
    case 'tapeStop':
      return 'stop.circle';
    case 'glitchStutter':
      return 'rectangle.split.3x1.fill';
    case 'telephoneBlip':
      return 'phone.fill';
    case 'laser':
      return 'bolt.horizontal.fill';
    case 'zap':
      return 'bolt.fill';
    case 'staticBurst':
      return 'aqi.medium';
    case 'voxChopAh':
    case 'voxChopRise':
    case 'voxAir':
      return 'mouth.fill';
  }
}

/**
 * Loudness target in dBFS. Sustained textures sit back; short transients are allowed a hotter
 * average because their crest factor puts the peak in the same place.
 */
function effectTargetRMSDB(effect: LibraryEffect): number {
  switch (effect) {
    case 'impact':
    case 'subDrop':
      return -17;
    case 'riserShort':
    case 'riserLong':
    case 'downlifter':
    case 'noiseSweepUp':
    case 'noiseSweepDown':
    case 'vinylCrackle':
    case 'siren':
    case 'voxAir':
    case 'reverseSwell':
    case 'tapeStop':
      return -18;
    default:
      return -20;
  }
}

/**
 * Set only where the sound has one steady, sounding pitch. A riser, a siren and a sub drop are
 * all pitched at *some* moment and at no particular one, and tagging them with a root would be
 * a promise the audio does not keep.
 */
function effectRootMidi(effect: LibraryEffect): number | undefined {
  return effect === 'voxChopAh' ? 60 : undefined;
}

function effectSlug(effect: LibraryEffect): string {
  return `fx-${effectToken(effect)}`;
}

// MARK: - Patch builders shared between cases

/**
 * The build lives in the *filter* envelope, not the amplitude one: a band-pass opening six
 * octaves is what makes a riser feel like it is climbing, while the amplitude envelope only
 * decides how loud the climb is.
 */
function riserPatch(effect: LibraryEffect, seconds: number, echo: boolean): SynthPatch {
  return makeSynthPatch({
    name: effectDisplayName(effect),
    symbolName: effectSymbol(effect),
    layers: [
      makeSynthLayer({ waveform: 'noise', level: 1.0 }),
      makeSynthLayer({ waveform: 'saw', level: 0.3, unisonVoices: 4, unisonDetuneCents: 30 }),
    ],
    amplitude: makeSynthEnvelope({
      attack: seconds * 0.82,
      hold: 0.05,
      decay: 0.2,
      sustain: 0.88,
      release: seconds * 0.16,
      curve: -0.5,
    }),
    filter: makeSynthFilter({
      type: 'bandPass',
      cutoff: 180,
      resonance: 4.5,
      envelopeAmount: 6.0,
      envelope: makeSynthEnvelope({
        attack: seconds * 0.92,
        decay: 0.3,
        sustain: 0.9,
        release: seconds * 0.15,
        curve: -0.3,
      }),
    }),
    // Negative, so the pitched layer glides *up* into the drop.
    pitchEnvelope: makeSynthPitchEnvelope({ amountSemitones: -18, decay: seconds * 0.9, curve: -0.5 }),
    baseFrequency: 220,
    duration: seconds,
    drive: 0.18,
    chain: echo
      ? makeEffectChain([
          makeEffectNode('delay', { values: { time: 0.33, feedback: 0.4, tone: 5000, sync: 0, mix: 0.22 } }),
        ])
      : makeEffectChain(),
    seed: librarySeed(effectSlug(effect), 0),
  });
}

/**
 * Same band-pass, same five-octave throw, opposite envelope. Rising sweeps climb on the
 * filter's *attack*; falling sweeps ride its decay back down. Flipping the envelope rather
 * than the amount keeps both ends of both sweeps at the same two frequencies, so they answer
 * each other.
 */
function sweepPatch(effect: LibraryEffect, rising: boolean): SynthPatch {
  return makeSynthPatch({
    name: effectDisplayName(effect),
    symbolName: effectSymbol(effect),
    layers: [makeSynthLayer({ waveform: 'noise', level: 1.0 })],
    amplitude: rising
      ? makeSynthEnvelope({ attack: 1.3, hold: 0.06, decay: 0.2, sustain: 0.85, release: 0.25, curve: -0.4 })
      : makeSynthEnvelope({ attack: 0.05, hold: 0.1, decay: 1.4, sustain: 0.05, release: 0.25, curve: 0.3 }),
    filter: makeSynthFilter({
      type: 'bandPass',
      cutoff: 260,
      resonance: 2.4,
      envelopeAmount: 5.2,
      envelope: rising
        ? makeSynthEnvelope({ attack: 1.45, decay: 0.2, sustain: 0.9, release: 0.25, curve: -0.3 })
        : makeSynthEnvelope({ attack: 0.03, decay: 1.5, sustain: 0.02, release: 0.2, curve: 0.2 }),
    }),
    lfo: makeSynthLFO({ isEnabled: true, waveform: 'sine', rateHz: 0.5, depth: 0.55, target: 'pan' }),
    baseFrequency: 900,
    duration: 1.8,
    drive: 0.1,
    seed: librarySeed(effectSlug(effect), 0),
  });
}

/**
 * The vocal chop source. Negative glide sweeps *up* into the note; zero holds it steady,
 * which is what the chop needs so its root tag means something.
 */
function voxPatch(effect: LibraryEffect, frequency: number, glide: number): SynthPatch {
  return makeSynthPatch({
    name: effectDisplayName(effect),
    symbolName: effectSymbol(effect),
    layers: [
      makeSynthLayer({ waveform: 'saw', level: 1.0, unisonVoices: 2, unisonDetuneCents: 7 }),
      makeSynthLayer({ waveform: 'triangle', semitones: 12, level: 0.16 }),
    ],
    amplitude: makeSynthEnvelope({ attack: 0.03, hold: 0.16, decay: 0.28, sustain: 0.25, release: 0.2, curve: 0.3 }),
    filter: makeSynthFilter({ type: 'lowPass', cutoff: 4200, resonance: 0.8, keyTracking: 0.3 }),
    pitchEnvelope: makeSynthPitchEnvelope({ amountSemitones: glide, decay: 0.12, curve: -0.3 }),
    lfo: makeSynthLFO({ isEnabled: true, waveform: 'sine', rateHz: 5.2, depth: 0.004, target: 'pitch' }),
    baseFrequency: frequency,
    duration: 0.7,
    drive: 0.12,
    seed: librarySeed(effectSlug(effect), 0),
  });
}

// MARK: - Assembled sounds

/**
 * A seamless vinyl bed: surface hiss, turntable rumble, and randomly placed pops.
 *
 * The pops are the sound. Hiss alone is a noise gate's problem; what the ear hears as "vinyl"
 * is a sparse, irregular train of very short clicks with a wide dynamic range, which is why
 * they are placed by a Poisson-like draw rather than on any grid. The whole bed is rendered a
 * quarter of a second long and then crossfaded into itself, so the end meets the beginning
 * with no seam to hear.
 */
export function renderVinylCrackle(seconds: number, sampleRate: number): Float32Array[] {
  const crossfade = 0.25;
  const total = Math.max(64, Math.trunc((seconds + crossfade) * sampleRate));
  const random = libraryRandom('fx-vinyl-crackle', 0);

  const channels = [new Float32Array(total), new Float32Array(total)];

  // Surface hiss and rumble, generated per channel from independent draws so the bed is wide
  // rather than a mono texture pretending to be stereo.
  for (const channel of channels) {
    const hiss = new Biquad().set('bandPass', 3400, 0.5, sampleRate);
    const rumble = new Biquad().set('lowPass', 58, 0.9, sampleRate);
    for (let index = 0; index < total; index++) {
      const white = random.unit() * 2 - 1;
      const low = random.unit() * 2 - 1;
      channel[index] = hiss.process(white) * 0.14 + rumble.process(low) * 0.55;
    }
  }

  // ~17 pops a second, each a click through its own resonator so no two have the same colour.
  // Amplitudes are squared to make small pops far more common than loud ones, which is what a
  // worn record actually sounds like.
  const popCount = Math.max(1, Math.trunc((total / sampleRate) * 17));
  const left = channels[0]!;
  const right = channels[1]!;
  for (let pop = 0; pop < popCount; pop++) {
    const start = Math.trunc(random.unit() * (total - 1));
    const amplitude = Math.pow(random.range(0.15, 1.0), 2) * 0.9;
    const decay = random.range(0.0006, 0.004);
    const colour = random.range(1200, 6000);
    const gains = panGains(random.range(-0.8, 0.8));
    const length = Math.min(Math.trunc(decay * 6 * sampleRate), total - start);
    if (length <= 2) continue;

    const resonator = new Biquad().set('bandPass', colour, 3.5, sampleRate);
    const burst = libraryRandom('fx-vinyl-crackle-pop', start);
    for (let offset = 0; offset < length; offset++) {
      const time = offset / sampleRate;
      const value = resonator.process(burst.unit() * 2 - 1) * amplitude * Math.exp(-time / decay);
      left[start + offset]! += value * gains.left;
      right[start + offset]! += value * gains.right;
    }
  }

  crossfadeLoop(channels, Math.trunc(crossfade * sampleRate));
  return channels;
}

/**
 * Slows a buffer to a standstill.
 *
 * The read position advances by a rate that falls from 1 to 0 on a curve, and the low-pass
 * closes with it. Both halves matter: a rate ramp on its own sounds like a pitch bend, and it
 * only becomes *tape* once the top end goes with the speed, the way head contact loss does on
 * a real machine.
 */
export function applyTapeStop(source: readonly Float32Array[], seconds: number, sampleRate: number): Float32Array[] {
  const sourceFrames = frameCount(source);
  if (sourceFrames <= 4 || source.length === 0) return source.map((channel) => channel.slice());

  const outputFrames = Math.max(8, Math.trunc(seconds * sampleRate));
  const output = source.map(() => new Float32Array(outputFrames));
  const filters = source.map(() => new Biquad());

  let position = 0;
  const lastReadable = sourceFrames - 1;

  for (let frame = 0; frame < outputFrames; frame++) {
    const progress = frame / (outputFrames - 1);
    // 1.6 rather than 1: a capstan does not decelerate linearly, it drags.
    const rate = Math.pow(1 - progress, 1.6);
    if (frame % 32 === 0) {
      const cutoff = Math.max(280, 16000 * Math.pow(Math.max(rate, 0.02), 0.85));
      for (const filter of filters) filter.set('lowPass', cutoff, 0.707, sampleRate);
    }
    for (let channel = 0; channel < source.length; channel++) {
      const value = interpolateCatmullRom(source[channel]!, Math.min(position, lastReadable));
      output[channel]![frame] = filters[channel]!.process(value);
    }
    position += rate;
  }

  applyEdgeFades(output, 0.004, 0.03, sampleRate);
  return output;
}

/**
 * The material the glitch burst chews on: a plucked note with sixteenth-note hats over it at
 * 140 BPM. Both are needed — stuttering a sustained tone gives a rhythm with no transients,
 * and stuttering hats alone gives transients with no pitch to jump.
 */
async function glitchSource(context: LibraryRenderContext): Promise<Float32Array[]> {
  const sampleRate = context.sampleRate;
  const budget = context.budget;
  const tempo = 140;
  const sixteenth = 60 / tempo / 4;
  const frames = Math.max(64, Math.trunc(1.4 * sampleRate));
  const mix = [new Float32Array(frames), new Float32Array(frames)];

  const note = await renderInstrumentNote('pluck', 60, context);
  mixInto(mix, note.channels, 0, 0.8, 0);

  const hat = await renderPatch(percussionPatch('hihatClosed'), {
    sampleRate,
    ...(budget === undefined ? {} : { budget }),
  });
  const random = libraryRandom('fx-glitch-stutter', 1);
  let step = 0;
  while (step * sixteenth < 1.4) {
    const frame = Math.trunc(step * sixteenth * sampleRate);
    const gain = step % 4 === 0 ? 0.55 : random.range(0.22, 0.38);
    mixInto(mix, hat.channels, frame, gain, random.range(-0.3, 0.3));
    step++;
  }
  return mix;
}

// MARK: - Rendering

async function renderRaw(effect: LibraryEffect, context: LibraryRenderContext): Promise<Float32Array[]> {
  const sampleRate = context.sampleRate;
  const budget = context.budget;
  const patchOptions = { sampleRate, ...(budget === undefined ? {} : { budget }) };
  const chainContext = { sampleRate, tempo: 120, ...(budget === undefined ? {} : { budget }) };
  const slug = effectSlug(effect);
  const seed = librarySeed(slug, 0);
  const name = effectDisplayName(effect);
  const symbolName = effectSymbol(effect);

  switch (effect) {
    case 'riserShort':
      return (await renderPatch(riserPatch(effect, 2.0, false), patchOptions)).channels;

    case 'riserLong':
      return (await renderPatch(riserPatch(effect, 4.0, true), patchOptions)).channels;

    case 'downlifter':
      return (
        await renderPatch(
          makeSynthPatch({
            name,
            symbolName,
            layers: [
              makeSynthLayer({ waveform: 'saw', level: 0.85, unisonVoices: 3, unisonDetuneCents: 24 }),
              makeSynthLayer({ waveform: 'noise', level: 0.35 }),
            ],
            amplitude: makeSynthEnvelope({ attack: 0.02, hold: 0.12, decay: 2.2, sustain: 0, release: 0.25, curve: 0.25 }),
            // Positive envelope amount with a decaying envelope closes the filter: it starts
            // five octaves above the parked cutoff and falls onto it.
            filter: makeSynthFilter({
              type: 'lowPass',
              cutoff: 260,
              resonance: 2.6,
              envelopeAmount: 5.2,
              envelope: makeSynthEnvelope({ attack: 0.01, decay: 2.1, sustain: 0, release: 0.2, curve: 0.2 }),
            }),
            pitchEnvelope: makeSynthPitchEnvelope({ amountSemitones: 36, decay: 2.2, curve: 0.2 }),
            baseFrequency: 110,
            duration: 2.6,
            drive: 0.25,
            seed,
          }),
          patchOptions,
        )
      ).channels;

    case 'impact':
      return (
        await renderPatch(
          makeSynthPatch({
            name,
            symbolName,
            layers: [
              makeSynthLayer({ waveform: 'sine', level: 1.0 }),
              makeSynthLayer({ waveform: 'noise', level: 0.55 }),
              makeSynthLayer({ waveform: 'triangle', semitones: 19, level: 0.2 }),
            ],
            amplitude: makeSynthEnvelope({ attack: 0.001, hold: 0.01, decay: 1.5, sustain: 0, release: 0.2, curve: 0.55 }),
            filter: makeSynthFilter({
              type: 'lowPass',
              cutoff: 340,
              resonance: 1.0,
              envelopeAmount: 4.6,
              envelope: makeSynthEnvelope({ attack: 0.001, decay: 0.22, sustain: 0, release: 0.1, curve: 0.8 }),
            }),
            pitchEnvelope: makeSynthPitchEnvelope({ amountSemitones: 26, decay: 0.11, curve: 0.8 }),
            baseFrequency: 38,
            // The hit is over inside two seconds; the rest of the duration exists so the
            // reverb tail is rendered rather than truncated.
            duration: 3.2,
            drive: 0.5,
            chain: makeEffectChain([
              makeEffectNode('reverb', { values: { size: 0.9, damping: 0.3, preDelay: 0.025, mix: 0.45 } }),
            ]),
            seed,
          }),
          patchOptions,
        )
      ).channels;

    case 'subDrop':
      return (
        await renderPatch(
          makeSynthPatch({
            name,
            symbolName,
            layers: [
              makeSynthLayer({ waveform: 'sine', level: 1.0, resetsPhase: true, startPhase: 0 }),
              makeSynthLayer({ waveform: 'triangle', level: 0.14 }),
            ],
            amplitude: makeSynthEnvelope({ attack: 0.004, hold: 0.25, decay: 1.6, sustain: 0, release: 0.2, curve: 0.3 }),
            filter: makeSynthFilter({ type: 'lowPass', cutoff: 220, resonance: 0.9 }),
            // Twenty semitones of fall from an octave and a half above 28 Hz — starts around
            // 88 Hz and sinks below what a phone can reproduce, which is exactly the effect.
            pitchEnvelope: makeSynthPitchEnvelope({ amountSemitones: 20, decay: 1.7, curve: 0.35 }),
            baseFrequency: 28,
            duration: 2.2,
            // Drive is not decoration here: it puts harmonics above the sub so the drop is
            // still audible on a speaker with no bottom octave at all.
            drive: 0.3,
            seed,
          }),
          patchOptions,
        )
      ).channels;

    case 'reverseSwell': {
      const channels = (
        await renderPatch(
          makeSynthPatch({
            name,
            symbolName,
            layers: [
              makeSynthLayer({ waveform: 'noise', level: 0.9 }),
              makeSynthLayer({ waveform: 'square', level: 0.3 }),
              makeSynthLayer({ waveform: 'square', semitones: 6.4, level: 0.26 }),
            ],
            amplitude: makeSynthEnvelope({ attack: 0.001, hold: 0.01, decay: 2.1, sustain: 0, release: 0.15, curve: 0.4 }),
            filter: makeSynthFilter({
              type: 'highPass',
              cutoff: 3800,
              resonance: 0.8,
              envelopeAmount: -0.6,
              envelope: makeSynthEnvelope({ attack: 0.001, decay: 1.6, sustain: 0, release: 0.1, curve: 0.5 }),
            }),
            baseFrequency: 5200,
            duration: 2.4,
            drive: 0.12,
            chain: makeEffectChain([
              makeEffectNode('reverb', { values: { size: 0.55, damping: 0.4, preDelay: 0.01, mix: 0.22 } }),
            ]),
            seed,
          }),
          patchOptions,
        )
      ).channels;
      // A cymbal decay played backwards is a cymbal swell. Reversing after the reverb means
      // the room arrives *before* the hit, which is the part that makes it read as "something
      // is about to happen".
      reverseChannels(channels);
      applyEdgeFades(channels, 0.02, 0.004, sampleRate);
      return channels;
    }

    case 'noiseSweepUp':
      return (await renderPatch(sweepPatch(effect, true), patchOptions)).channels;

    case 'noiseSweepDown':
      return (await renderPatch(sweepPatch(effect, false), patchOptions)).channels;

    case 'vinylCrackle':
      return renderVinylCrackle(4.0, sampleRate);

    case 'tapeStop': {
      const source = await renderPatch(
        makeSynthPatch({
          name: 'Tape Source',
          symbolName,
          layers: [
            makeSynthLayer({ waveform: 'saw', level: 0.9, unisonVoices: 3, unisonDetuneCents: 12 }),
            makeSynthLayer({ waveform: 'square', semitones: 7, level: 0.35 }),
            makeSynthLayer({ waveform: 'saw', semitones: 12, level: 0.25 }),
          ],
          amplitude: makeSynthEnvelope({ attack: 0.01, hold: 0.4, decay: 0.4, sustain: 0.8, release: 0.1, curve: 0 }),
          filter: makeSynthFilter({ type: 'lowPass', cutoff: 5200, resonance: 1.2 }),
          baseFrequency: 174.61, // F3
          duration: 1.3,
          drive: 0.2,
          seed,
        }),
        patchOptions,
      );
      return applyTapeStop(source.channels, 1.7, sampleRate);
    }

    case 'glitchStutter': {
      // Beat repeat first, then slice mangling: stuttering already-glitched audio repeats the
      // glitch, while glitching already-stuttered audio breaks up the repeat — which is the
      // sound people mean by "glitch burst".
      let channels = await glitchSource(context);
      channels = await applyEffectChain(
        channels,
        makeEffectChain([
          makeEffectNode('stutter', { values: { division: 3, gateLength: 0.55, mix: 1.0 } }),
          makeEffectNode('glitch', {
            values: {
              density: 0.62,
              sliceSize: 0.035,
              reverseChance: 0.35,
              pitchChance: 0.3,
              seed: librarySeedNumber(slug, 2),
            },
          }),
          makeEffectNode('bitCrush', { values: { bits: 10, mix: 0.35 } }),
          makeEffectNode('compressor', {
            values: { threshold: -18, ratio: 4, attack: 0.002, release: 0.08, makeup: 3 },
          }),
        ]),
        { ...chainContext, tempo: 140 },
      );
      applyEdgeFades(channels, 0.002, 0.01, sampleRate);
      return channels;
    }

    case 'telephoneBlip':
      return (
        await renderPatch(
          makeSynthPatch({
            name,
            symbolName,
            layers: [
              makeSynthLayer({ waveform: 'square', level: 0.85 }),
              makeSynthLayer({ waveform: 'sine', semitones: 7, level: 0.45 }),
            ],
            amplitude: makeSynthEnvelope({
              attack: 0.004,
              hold: 0.06,
              decay: 0.05,
              sustain: 0,
              release: 0.02,
              curve: 0.3,
              burstCount: 2,
              burstInterval: 0.12,
            }),
            filter: makeSynthFilter({ type: 'lowPass', cutoff: 3400, resonance: 1.0 }),
            baseFrequency: 1046.5, // C6 — inside the telephone band, not near its edge
            duration: 0.34,
            drive: 0.2,
            chain: makeEffectChain([
              // The 300–3200 Hz band is the whole illusion: a handset has no bass and no air,
              // and nothing else about the tone matters.
              makeEffectNode('highPass', { values: { frequency: 320, resonance: 3 } }),
              makeEffectNode('lowPass', { values: { frequency: 3200, resonance: 4 } }),
              makeEffectNode('bitCrush', { values: { bits: 9, mix: 0.45 } }),
              makeEffectNode('distortion', { values: { drive: 0.3, tone: 0.7, character: 0, mix: 0.4 } }),
            ]),
            seed,
          }),
          patchOptions,
        )
      ).channels;

    case 'laser':
      return (
        await renderPatch(
          makeSynthPatch({
            name,
            symbolName,
            layers: [makeSynthLayer({ waveform: 'saw', level: 1.0, unisonVoices: 3, unisonDetuneCents: 18 })],
            amplitude: makeSynthEnvelope({ attack: 0.001, hold: 0.01, decay: 0.35, sustain: 0, release: 0.06, curve: 0.5 }),
            filter: makeSynthFilter({
              type: 'lowPass',
              cutoff: 7000,
              resonance: 3.0,
              envelopeAmount: 1.4,
              envelope: makeSynthEnvelope({ attack: 0.001, decay: 0.3, sustain: 0, release: 0.05, curve: 0.6 }),
              keyTracking: 0.5,
            }),
            pitchEnvelope: makeSynthPitchEnvelope({ amountSemitones: 48, decay: 0.34, curve: 0.45 }),
            baseFrequency: 260,
            duration: 0.62,
            drive: 0.35,
            chain: makeEffectChain([
              makeEffectNode('delay', { values: { time: 0.07, feedback: 0.34, tone: 5200, sync: 0, mix: 0.25 } }),
            ]),
            seed,
          }),
          patchOptions,
        )
      ).channels;

    case 'zap':
      return (
        await renderPatch(
          makeSynthPatch({
            name,
            symbolName,
            layers: [
              makeSynthLayer({ waveform: 'square', level: 0.75 }),
              makeSynthLayer({ waveform: 'saw', level: 0.55, unisonVoices: 2, unisonDetuneCents: 24 }),
              makeSynthLayer({ waveform: 'noise', level: 0.3 }),
            ],
            amplitude: makeSynthEnvelope({
              attack: 0.0004,
              hold: 0.004,
              decay: 0.15,
              sustain: 0,
              release: 0.02,
              curve: 0.85,
            }),
            filter: makeSynthFilter({
              type: 'bandPass',
              cutoff: 2400,
              resonance: 2.2,
              envelopeAmount: 1.6,
              envelope: makeSynthEnvelope({ attack: 0.0005, decay: 0.07, sustain: 0, release: 0.02, curve: 0.85 }),
            }),
            pitchEnvelope: makeSynthPitchEnvelope({ amountSemitones: 26, decay: 0.045, curve: 0.85 }),
            // 120 Hz is far above vibrato and inside the audible band, so the LFO stops being
            // movement and becomes part of the timbre.
            lfo: makeSynthLFO({ isEnabled: true, waveform: 'square', rateHz: 120, depth: 0.07, target: 'pitch' }),
            baseFrequency: 780,
            duration: 0.28,
            drive: 0.55,
            seed,
          }),
          patchOptions,
        )
      ).channels;

    case 'siren':
      return (
        await renderPatch(
          makeSynthPatch({
            name,
            symbolName,
            layers: [
              makeSynthLayer({ waveform: 'saw', level: 0.9, unisonVoices: 2, unisonDetuneCents: 12 }),
              makeSynthLayer({ waveform: 'square', semitones: 12, level: 0.22 }),
            ],
            amplitude: makeSynthEnvelope({ attack: 0.15, hold: 1.6, decay: 0.3, sustain: 0.6, release: 0.5, curve: -0.2 }),
            filter: makeSynthFilter({
              type: 'lowPass',
              cutoff: 2600,
              resonance: 2.0,
              envelopeAmount: 0.8,
              envelope: makeSynthEnvelope({ attack: 0.4, decay: 1.2, sustain: 0.5, release: 0.4, curve: 0 }),
              keyTracking: 0.4,
            }),
            lfo: makeSynthLFO({ isEnabled: true, waveform: 'triangle', rateHz: 0.75, depth: 0.3, target: 'pitch' }),
            baseFrequency: 620,
            duration: 2.6,
            drive: 0.3,
            seed,
          }),
          patchOptions,
        )
      ).channels;

    case 'staticBurst':
      return (
        await renderPatch(
          makeSynthPatch({
            name,
            symbolName,
            layers: [makeSynthLayer({ waveform: 'noise', level: 1.0 })],
            amplitude: makeSynthEnvelope({
              attack: 0.002,
              hold: 0.05,
              decay: 0.28,
              sustain: 0.1,
              release: 0.12,
              curve: 0.4,
              burstCount: 3,
              burstInterval: 0.09,
            }),
            filter: makeSynthFilter({
              type: 'bandPass',
              cutoff: 1800,
              resonance: 1.4,
              envelopeAmount: 1.8,
              envelope: makeSynthEnvelope({ attack: 0.002, decay: 0.2, sustain: 0.1, release: 0.1, curve: 0.5 }),
            }),
            baseFrequency: 1800,
            duration: 0.55,
            drive: 0.2,
            chain: makeEffectChain([
              makeEffectNode('sampleRateReduce', { values: { rate: 6000, mix: 0.6 } }),
              makeEffectNode('bitCrush', { values: { bits: 6, mix: 0.5 } }),
            ]),
            seed,
          }),
          patchOptions,
        )
      ).channels;

    case 'metalClank':
      return (
        await renderPatch(
          makeSynthPatch({
            name,
            symbolName,
            layers: [
              // Deliberately irrational intervals. Tune these to anything consonant and it
              // stops being metal and becomes a chord.
              makeSynthLayer({ waveform: 'square', level: 0.8 }),
              makeSynthLayer({ waveform: 'square', semitones: 6.4, level: 0.65 }),
              makeSynthLayer({ waveform: 'square', semitones: 11.3, level: 0.45 }),
            ],
            amplitude: makeSynthEnvelope({
              attack: 0.0005,
              hold: 0.004,
              decay: 0.12,
              sustain: 0.18,
              release: 0.55,
              curve: 0.75,
            }),
            filter: makeSynthFilter({
              type: 'bandPass',
              cutoff: 2600,
              resonance: 4.5,
              envelopeAmount: 1.1,
              envelope: makeSynthEnvelope({ attack: 0.0005, decay: 0.1, sustain: 0.1, release: 0.3, curve: 0.7 }),
            }),
            pitchEnvelope: makeSynthPitchEnvelope({ amountSemitones: 4, decay: 0.01, curve: 0.9 }),
            baseFrequency: 620,
            duration: 0.95,
            drive: 0.4,
            chain: makeEffectChain([
              makeEffectNode('reverb', { values: { size: 0.4, damping: 0.25, preDelay: 0.006, mix: 0.3 } }),
            ]),
            seed,
          }),
          patchOptions,
        )
      ).channels;

    case 'powerDown':
      return (
        await renderPatch(
          makeSynthPatch({
            name,
            symbolName,
            layers: [
              makeSynthLayer({ waveform: 'saw', level: 0.8, unisonVoices: 2, unisonDetuneCents: 16 }),
              makeSynthLayer({ waveform: 'square', semitones: 12, level: 0.35 }),
            ],
            amplitude: makeSynthEnvelope({ attack: 0.01, hold: 0.08, decay: 1.3, sustain: 0, release: 0.2, curve: 0.35 }),
            filter: makeSynthFilter({
              type: 'lowPass',
              cutoff: 300,
              resonance: 3.4,
              envelopeAmount: 4.8,
              envelope: makeSynthEnvelope({ attack: 0.008, decay: 1.2, sustain: 0, release: 0.15, curve: 0.3 }),
            }),
            pitchEnvelope: makeSynthPitchEnvelope({ amountSemitones: 30, decay: 1.35, curve: 0.15 }),
            baseFrequency: 90,
            duration: 1.6,
            drive: 0.3,
            chain: makeEffectChain([makeEffectNode('bitCrush', { values: { bits: 8, mix: 0.3 } })]),
            seed,
          }),
          patchOptions,
        )
      ).channels;

    case 'voxChopAh': {
      const channels = (await renderPatch(voxPatch(effect, 261.6255653, 0), patchOptions)).channels;
      applyFormantBank(channels, 'ah', sampleRate);
      return applyEffectChain(
        channels,
        makeEffectChain([
          makeEffectNode('highPass', { values: { frequency: 110, resonance: 0 } }),
          makeEffectNode('delay', { values: { time: 0.14, feedback: 0.2, tone: 3800, sync: 0, mix: 0.2 } }),
        ]),
        chainContext,
      );
    }

    case 'voxChopRise': {
      const channels = (await renderPatch(voxPatch(effect, 329.6276, -7), patchOptions)).channels;
      applyFormantBank(channels, 'oh', sampleRate);
      // Formants up, pitch untouched: the shifter re-applies the spectral envelope as a
      // per-bin gain, so the harmonics never move. That is what makes this sound like a
      // smaller singer rather than a sped-up tape.
      return applyEffectChain(
        channels,
        makeEffectChain([makeEffectNode('formantShift', { values: { shift: 5 } })]),
        chainContext,
      );
    }

    case 'voxAir': {
      const channels = (
        await renderPatch(
          makeSynthPatch({
            name,
            symbolName,
            layers: [makeSynthLayer({ waveform: 'noise', level: 1.0 })],
            amplitude: makeSynthEnvelope({ attack: 0.6, hold: 0.2, decay: 0.7, sustain: 0.35, release: 0.7, curve: -0.4 }),
            filter: makeSynthFilter({ type: 'bandPass', cutoff: 1400, resonance: 0.7 }),
            baseFrequency: 1400,
            duration: 2.5,
            drive: 0.05,
            chain: makeEffectChain([
              makeEffectNode('reverb', { values: { size: 0.6, damping: 0.5, preDelay: 0.02, mix: 0.3 } }),
            ]),
            seed,
          }),
          patchOptions,
        )
      ).channels;
      // Breath through a vowel shape. No oscillator at all, so there is no pitch to clash with
      // anything the sound is laid over.
      applyFormantBank(channels, 'ee', sampleRate, 0.3);
      return channels;
    }
  }
}

export async function renderLibraryEffect(
  effect: LibraryEffect,
  context: LibraryRenderContext,
): Promise<RenderedAudio> {
  const channels = await renderRaw(effect, context);
  finishToRMS(channels, effectTargetRMSDB(effect));
  return { channels, sampleRate: context.sampleRate };
}

// MARK: - Catalogue

export const fxEntries: readonly LibraryEntry[] = LIBRARY_EFFECTS.map((effect) => {
  const root = effectRootMidi(effect);
  return makeLibraryEntry({
    id: effectSlug(effect),
    fileName: `fx-${effectToken(effect)}.wav`,
    name: effectDisplayName(effect),
    caption: effectCaption(effect),
    tags: effectTags(effect),
    family: 'fx',
    group: effectDisplayName(effect),
    color: effectColor(effect),
    symbolName: effectSymbol(effect),
    ...(root === undefined ? {} : { rootMidi: root }),
    // Only the crackle bed is built to be held down and repeated.
    loops: effect === 'vinylCrackle',
    render: (context) => renderLibraryEffect(effect, context),
  });
});
